import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { BookService } from '../../services/book.services';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { IMultiSelectOption } from 'angular-2-dropdown-multiselect';
import { IMyDpOptions } from 'mydatepicker';
import { ImageResult } from 'ngx-image2dataurl';

import { DyspachApiService, DyspachQuoteResponse } from '../../services/dyspach-api.service';
import { DyspachMapperService } from '../../services/dyspach-mapper.service';
import { Idrv5ApiService, Idrv5QuoteDetailsResponse } from '../../services/idrv5-api.service';
import { Idrv5MapperService } from '../../services/idrv5-mapper.service';

@Component({
  selector: 'app-booking',
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss'],
  providers: [BookService, ToastrService, DyspachApiService, DyspachMapperService, Idrv5ApiService, Idrv5MapperService]
})
export class BookingComponent implements OnInit {
  bookingForm: FormGroup;
  quoteData: any;
  loading = true;
  /** Tracks how many async data loads are still in-flight.
   *  loading stays true until every pending load has completed. */
  private loadingCount = 0;
  activeTab = 'load'; // Default active tab
  CHARGING_METHOD_ID = {
    LOAD: 1
  }
  
  // Development mode flag
  isDevelopmentMode = true; // Set this to false when deploying

  quote_id : number;
  quoteSub: Subscription;
  quote_reference_no: string = '';
  terms : any;
  constants: any;

  currentPickupIndex = 0;
  currentDeliveryIndex = 0;

  pointdata: any;
  public mapObj: any;
  public mapLat: any;
  public mapLng: any;

  positions = [];
  states: any;
  est_total_price = 0;

  public userSettings: any = {
    showSearchButton: false,
    inputPlaceholderText: 'Type key word on location to search',
    showCurrentLocation: false
  };

  public customers: IMultiSelectOption[];
  public customerControlOptions: IMultiSelectOption[];

  currentDate = new Date();
  public myDatePickerOptions: IMyDpOptions = {
    // other options...
    dateFormat: 'dd mmm yyyy',
    height: '25px',
    selectionTxtFontSize: '13px',
    showTodayBtn: true,
    disableUntil: {year: this.currentDate.getFullYear(), month: this.currentDate.getMonth()+1, day: this.currentDate.getDate()-1}

  };

  public doc_file: string;
  public dg_file: string;

  public premise_types: any;

  isFormValid = false;

  private pickupAddressesChanged: boolean[] = [];
  private deliveryAddressesChanged: boolean[] = [];
  private currentPickupAddresses: string[] = [];
  private currentDeliveryAddresses: string[] = [];

  dyspachQuoteRequestId: string = '';
  dyspachQuoteResponse: DyspachQuoteResponse;
  isDyspachBooking: boolean = false;

  isIdrv5Booking: boolean = false;
  idrv5QuoteRequestId: string = '';
  idrv5ServiceLevelId: string | undefined = undefined;  // persists URL param through async flows
  idrv5QuoteResponse: Idrv5QuoteDetailsResponse;
  
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private bookService: BookService,
    private toastr: ToastrService,
    private _Activatedroute: ActivatedRoute,
    private dyspachApiService: DyspachApiService,
    private dyspachMapperService: DyspachMapperService,
    private idrv5ApiService: Idrv5ApiService,
    private idrv5MapperService: Idrv5MapperService
  ) { 
    // Hide the global preloader when accessing this component directly
    document.getElementById('preloader').classList.add('hide'); 
  }

  ngOnInit(): void {
    this.initializeForm();
    this.getBookingData();

    // Handle route params (internal quote IDs via /booking/:quote_id)
    this._Activatedroute.params.subscribe(params => {
      console.log('Route params:', params);

      if (params['quote_id']) {
        // Standard internal quote ID — e.g. /booking/123
        this.quote_id = params['quote_id'];
        console.log('Processing internal quote_id from route params:', this.quote_id);
        this.checkForExistingBooking(this.quote_id, null);
        this.getQuoteData(this.quote_id);
      }
    });

    // Handle query params — covers iDrv5 widget, Dyspach, and legacy iDrv5 formats.
    //
    // iDrv5 widget (new format):
    //   /booking?quote_id_widget=544&service_level_id=2   ← both optional
    //   /booking?quote_id_widget=544                      ← service_level_id absent → use is_selected
    //
    // Dyspach:
    //   /booking?quoteID=xxx&rateID=yyy
    //
    // iDrv5 legacy:
    //   /booking?idrv5QuoteID=xxx&idrv5RateID=yyy
    this._Activatedroute.queryParams.subscribe(queryParams => {
      console.log('Query params:', queryParams);

      // ── iDrv5 widget (new format) ────────────────────────────────────────
      if (queryParams['quote_id_widget']) {
        const idrv5Id = String(queryParams['quote_id_widget']);
        // service_level_id is optional — when present it overrides is_selected
        const serviceLevelId: string | undefined = queryParams['service_level_id']
          ? String(queryParams['service_level_id'])
          : undefined;

        console.log('iDrv5 widget quote_id_widget:', idrv5Id, '| service_level_id:', serviceLevelId);

        this.isIdrv5Booking = true;
        this.idrv5QuoteRequestId = idrv5Id;
        this.idrv5ServiceLevelId = serviceLevelId;  // persist for proceedWithBookingFlow
        this.bookingForm.patchValue({ outside_quote_origin: 'idrv5' });
        this.checkForExistingBooking(null, idrv5Id);
        // getIdrv5Quote is called by proceedWithBookingFlow after the duplicate check
        return;
      }

      // ── Dyspach ──────────────────────────────────────────────────────────
      if (queryParams['quoteID']) {
        if (!queryParams['rateID']) {
          console.error('Missing required rateID parameter');
          this.toastr.error('Invalid booking link. Both Quote ID and Rate ID are required.', 'Error');
          this.router.navigate(['']);
          return;
        }

        this.isDyspachBooking = true;
        this.dyspachQuoteRequestId = queryParams['quoteID'];
        console.log('Found Dyspach quoteID in query params:', this.dyspachQuoteRequestId);
        this.bookingForm.patchValue({ outside_quote_origin: 'dyspach' });

        this.checkForExistingBooking(null, this.dyspachQuoteRequestId);
        this.getDyspachQuote(this.dyspachQuoteRequestId, queryParams['rateID']);
        return;
      }

      // ── iDrv5 legacy (idrv5QuoteID / idrv5RateID) ────────────────────────
      if (queryParams['idrv5QuoteID']) {
        this.isIdrv5Booking = true;
        this.idrv5QuoteRequestId = queryParams['idrv5QuoteID'];
        console.log('Found iDrv5 quoteID in query params (legacy):', this.idrv5QuoteRequestId);

        this.checkForExistingBooking(null, this.idrv5QuoteRequestId);
        this.getIdrv5Quote(this.idrv5QuoteRequestId);
        return;
      }
    });

    this.bookingForm.valueChanges.subscribe(() => {
      this.checkFormValidity();
    });
  
    this.checkFormValidity();
  }

  /**
   * Fetch quote data from Dyspach API
   * @param quoteRequestId The quote request ID from Dyspach
   * @param selectedRateId Optional selected rate ID
   */
  getDyspachQuote(quoteRequestId: string, specificRateId?: string) {
    console.log('getDyspachQuote called with:', 
      'quoteRequestId =', quoteRequestId, 
      'specificRateId  =', specificRateId);
    
    this.startLoading();
    this.dyspachApiService.getQuoteById(quoteRequestId).subscribe({
      next: (response) => {
        console.log('Dyspach quote response:', response);
        this.dyspachQuoteResponse = response;
        
        // Find the actual rate object, don't replace it with serviceClass
        let selectedRate = null;
        let serviceClass = null;
        
        if (specificRateId && response.quote.length > 0) {
          console.log('Looking for selected rate ID:', specificRateId);
          selectedRate = response.quote.find(rate => rate.rateId === specificRateId);

          if (selectedRate) {
            console.log('Found matching rate:', selectedRate);
            serviceClass = selectedRate.serviceClass; // Store service class separately
          } else {
            console.log('No matching rate found, using default');
            selectedRate = response.quote[0]; // Keep the full rate object
            serviceClass = selectedRate.serviceClass;
            console.log('Using default rate instead:', selectedRate);
          }
        } else {
          // No specific rate requested, use the first one
          if (response.quote && response.quote.length > 0) {
            selectedRate = response.quote[0]; // Keep the full rate object
            serviceClass = selectedRate.serviceClass;
            console.log('No specific rate requested. Using first rate:', selectedRate);
          } else {
            console.log('No rates available in the response');
          }
        }
        
        // Pass the service class as the second parameter, not the full rate
        // But the mapper should still have access to the full response to find the rate
        this.quoteData = this.dyspachMapperService.mapFromQuoteResponse(response, serviceClass);
        this.quote_reference_no = quoteRequestId;
        
        // Patch the form with the mapped data
        this.patchQuoteDataToForm();
        this.finishLoading();
      },
      error: (error) => {
        console.error('Error fetching Dyspach quote:', error);
        this.toastr.error('Failed to fetch quote details. Please try again.', 'Error');
        this.getQuoteData(quoteRequestId);
        this.finishLoading();
      }
    });
  }

  /**
   * Fetch quote data from the iDrv5 backend (GET /api/get_quote_details/:id).
   * Mirrors getDyspachQuote() in structure.
   *
   * @param quoteId        The numeric iDrv5 quote ID
   * @param serviceLevelId Optional — the service_level_id from the URL query
   *                       param. When present, selects the matching rate entry.
   *                       When absent, the is_selected entry is used instead.
   */
  getIdrv5Quote(quoteId: string, serviceLevelId?: string) {
    console.log('getIdrv5Quote called with:',
      'quoteId =', quoteId,
      'serviceLevelId =', serviceLevelId);

    this.startLoading();
    this.idrv5ApiService.getQuoteById(quoteId).subscribe({
      next: (response) => {
        console.log('iDrv5 quote response:', response);
        this.idrv5QuoteResponse = response;

        // Mapper handles rate-entry selection and all field mapping.
        // serviceLevelId selects by service_level_id; absent → use is_selected.
        this.quoteData = this.idrv5MapperService.mapFromQuoteResponse(response, serviceLevelId);
        this.quote_reference_no = this.quoteData.quote_number || quoteId;

        this.patchQuoteDataToForm();
        this.finishLoading();
      },
      error: (error) => {
        console.error('Error fetching iDrv5 quote:', error);
        this.toastr.error('Failed to fetch iDrv5 quote details. Please try again.', 'Error');
        this.finishLoading();
      }
    });
  }

  private initializeForm() {
    const currencyRegex = '^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$';

    this.bookingForm = this.fb.group({
      id: [null],
      outside_reference_no: [null],
      outside_quote_origin: ['internal'],  // 'internal' | 'dyspach' | 'idrv5'
      service_option: ['economy'],
      customer_reference_no: [''],
      charging_method_id: [this.CHARGING_METHOD_ID.LOAD],
      price: [''],
      is_dg: [this.quoteData?.is_dg || false],
      dgClass: this.initDGClassesGroup(),
      pickup_sites: this.initPickupSitesGroup(),
      delivery_sites: this.initDeliverySitesGroup(),
      customer: this.initCustomerFormGroup(),
      use_company_billing: [true],
      bill_to: this.initBillToFormGroup(),
      created_by: [1], // Replace with actual user ID
      packagings: this.fb.array([
        this.initPackagings()
      ]),
      total_cost: [0],
      tonnes: [1],
      pallet: [1],
      total_klms: [0],
      est_total_hours: [0],
      other_cost: [0],
      loads: [1],
      special_instructions: [''],
      quote_id: [null],
      connote_file: [],
      connote_filename: [],
      chargeable_pallets: [0],
      total_weight: [0],
      // addin_items: this.fb.array([ //What is this for?
      //   this.initAddinItem()
      // ])
    });
  }

  getQuoteData(quote_id){
    this.startLoading();
    this.bookService.getQuoteById(quote_id).subscribe(data=>{
      var quote = data['quote'];
      if(quote?.length >0){
        this.quoteData = quote[0];
        this.quote_reference_no = this.quoteData['quote_reference'];
        this.patchQuoteDataToForm();
      }
      
      console.log(this.quoteData, 'quote data');
      this.finishLoading();
    })
  }

  userMapping: any;
  getBookingData(){
    this.startLoading();
    this.bookService.getPublicBookingData().subscribe(data=>{
      console.log("Booking Data", data);
      this.terms = data['terms_of_service'];
      this.constants = data['constants'];
      if(this.constants?.length > 0){
        this.constants = data['constants'][0];
      }

      this.dgLicenses = data['dg_licenses'];
      this.packagingTypes = data['packaging_types'];
      this.costDefaults =  data['job_cost_defaults'];
      if(this.costDefaults.length){
        this.costDefaults = this.costDefaults[0];
      }
      this.premise_types = data['premise_types'];
      this.states = data['states'];
      this.customers = data['customers'];

      this.userMapping = data['user_mapping'];
      console.log('User mapping:', this.userMapping);

      this.finishLoading();
    })
  }

  validatePickupDeliverySequence() {
    const pickupSites = this.bookingForm.get('pickup_sites.addresses') as FormArray;
    const deliverySites = this.bookingForm.get('delivery_sites.addresses') as FormArray;
  
    // Check if we have sites to validate
    if (!pickupSites?.controls?.length || !deliverySites?.controls?.length) {
      return false;
    }

    // Collect all datetimes for duplicate check
    const allDateTimes = new Set<number>();
    let earliestPickup: Date | null = null;
    
    // Check pickup times
    for (const pickup of pickupSites.controls) {
      const date = pickup.get('date').value;
      const time = pickup.get('opening_time').value;
      
      if (date?.date && time) {
        const pickupTime = new Date(
          date.date.year,
          date.date.month - 1,
          date.date.day,
          time.hour || 0, 
          time.minute || 0
        );

        // Check for duplicates
        if (allDateTimes.has(pickupTime.getTime())) {
          return false;
        }
        allDateTimes.add(pickupTime.getTime());

        // Track earliest pickup
        if (!earliestPickup || pickupTime < earliestPickup) {
          earliestPickup = pickupTime;
        }
      }
    }

    // Check delivery times
    for (const delivery of deliverySites.controls) {
      const date = delivery.get('date').value;
      const time = delivery.get('opening_time').value;
      
      if (date?.date && time) {
        const deliveryTime = new Date(
          date.date.year,
          date.date.month - 1,
          date.date.day,
          time.hour || 0,
          time.minute || 0
        );

        // Check for duplicates
        if (allDateTimes.has(deliveryTime.getTime())) {
          return false;
        }
        allDateTimes.add(deliveryTime.getTime());

        // Check if this delivery is before earliest pickup
        if (earliestPickup && deliveryTime <= earliestPickup) {
          return false;
        }
      }
    }

    return true;
  }

  validateCustomerDetails() {
    const customerForm = this.bookingForm.get('customer');
    return customerForm.valid;
  }

  /** Increment the in-flight counter and ensure the spinner is shown. */
  private startLoading() {
    this.loadingCount++;
    this.loading = true;
  }

  /** Decrement the in-flight counter; hide the spinner only when all loads are done. */
  private finishLoading() {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    if (this.loadingCount === 0) {
      this.loading = false;
    }
  }

  checkFormValidity() {
    // Check if form controls are valid
    const formValid = this.bookingForm.valid;

    // Check load details
    const packagings = this.bookingForm.get('packagings') as FormArray;
    const packagingsValid = packagings.length > 0 && packagings.controls.every(packaging => {
      return packaging.get('packaging_type_id').valid &&
            packaging.get('quantity').valid &&
            packaging.get('length').valid &&
            packaging.get('width').valid &&
            packaging.get('height').valid &&
            packaging.get('weight').valid;
    });
    
    // Check customer details
    const customerValid = this.validateCustomerDetails();
  
    // Check sequence - handles all pickup/delivery order logic
    const sequenceValid = this.validatePickupDeliverySequence();

    // Check if all required addresses and dates are filled
    const pickupSites = this.bookingForm.get('pickup_sites.addresses') as FormArray;
    const deliverySites = this.bookingForm.get('delivery_sites.addresses') as FormArray;
    
    // Check complete addresses for both pickup and delivery
    const addressesValid = pickupSites.controls.every(control => 
      control.get('street').value &&
      control.get('suburb').value &&
      control.get('state').value &&
      control.get('postal_code').value
    ) && deliverySites.controls.every(control => 
      control.get('street').value &&
      control.get('suburb').value &&
      control.get('state').value &&
      control.get('postal_code').value 
    );

    // Check dates and times for both pickup and delivery
    const datesValid = pickupSites.controls.every(control => 
      control.get('date').value &&
      control.get('opening_time').value
    ) && deliverySites.controls.every(control => 
      control.get('date').value &&
      control.get('opening_time').value
    );
  
    // Update overall form validity
    this.isFormValid = formValid && packagingsValid && customerValid && sequenceValid && addressesValid && datesValid;

    // console.log("is valid?", {
    //   formValid,
    //   packagingsValid,
    //   customerValid,
    //   sequenceValid,
    //   addressesValid,
    //   datesValid
    // })
  }

  // Helper method to format date as required in MyDatePicker
  private formatDatePicker(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  }

  /**
   * Add N business days to a date, skipping Saturdays (6) and Sundays (0).
   * Returns a new Date object; the input is not mutated.
   */
  private addBusinessDays(startDate: Date, days: number): Date {
    const result = new Date(startDate);
    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) {
        remaining--;
      }
    }
    return result;
  }

  patchQuoteDataToForm(){
    console.log("Patching data to form", this.quoteData)
    // to do patch to booking form;
    var pkLength = this.quoteData.packagings.length;
    for (let count = 1; count < pkLength; count++) {
      this.addPackaging();
    }
    
    
    if(this.quoteData.is_dg == 1){
      this.showDGForm = true;
    }

    this.bookingForm.patchValue({
      quote_id: this.quoteData.id,
      is_dg: this.quoteData.is_dg,
      price: this.quoteData.est_total_price,
      packagings: this.quoteData.packagings,
      pallet: this.quoteData.chargeable_pallets,
      outside_reference_no: this.quoteData.outside_reference_no || null,
      special_instructions: this.quoteData.special_instructions || '',
      service_option: this.quoteData.service_option || 'economy',
    })

    // Pre-fill DG class when iDrv5 provides dg_licenses
    if (this.quoteData.is_dg == 1 && this.quoteData.dgClass) {
      const dg = this.quoteData.dgClass;

      // Patch scalar fields
      this.bookingForm.get('dgClass').patchValue({
        dg_class_id: dg.dg_class_id || '',
        dg_type_id:  dg.dg_type_id  || '1',
      });

      // Rebuild the subsidiaries FormArray
      const subsidiariesArray = this.bookingForm.get('dgClass.subsidiaries') as FormArray;
      subsidiariesArray.clear();
      (dg.subsidiaries || []).forEach((id: string) => {
        subsidiariesArray.push(new FormControl(id));
      });
    }

    // Pre-fill customer details when available (e.g. from iDrv5 quote header)
    if (this.quoteData.customer) {
      this.bookingForm.get('customer').patchValue({
        company:      this.quoteData.customer.company      || '',
        email:        this.quoteData.customer.email        || '',
        contact_no:   this.quoteData.customer.contact_no   || '',
        contact_name: this.quoteData.customer.contact_name || ''
      });
    }

    this.est_total_price = this.quoteData.est_total_price;

    for (let count = 0; count < pkLength; count++  ) {
      this.assignTotalWeight(count, 'component');
    }
    const pCtrl = (<any>this.bookingForm).controls.packagings;
    this.plc_packagings = pCtrl.value;

    // Patch pickup address
    const pickupSites = this.bookingForm.get('pickup_sites.addresses') as FormArray;
    const firstPickupAddress = pickupSites.at(0);

    // Patch delivery address
    const deliverySites = this.bookingForm.get('delivery_sites.addresses') as FormArray;
    const firstDeliveryAddress = deliverySites.at(0);

    // Handle the pickup date for MyDatePicker format
    if (this.quoteData.pickup_date) {
      const pickupDate = new Date(this.quoteData.pickup_date);
      
      // MyDatePicker expects this specific format
      const dateModel = {
          date: {
              year: pickupDate.getFullYear(),
              month: pickupDate.getMonth() + 1,
              day: pickupDate.getDate()
          },
          formatted: this.formatDatePicker(pickupDate), 
          jsdate: pickupDate,
          epoc: pickupDate.getTime()
      };
      
      firstPickupAddress.patchValue({
          date: dateModel
      });
    } else {
      // No pickup date from the API — use today.
      // Pre-fill the pickup time to now + 15 min, rounded up to the nearest
      // 15-min boundary to match the picker's minuteStep=15.
      // e.g. 13:43 + 15min = 13:58 → rounds up to 14:00
      //      13:40 + 15min = 13:55 → rounds up to 14:00
      //      13:30 + 15min = 13:45 → stays at 13:45
      const today = new Date();
      const dateModel = {
        date: {
          year: today.getFullYear(),
          month: today.getMonth() + 1,
          day: today.getDate()
        },
        formatted: this.formatDatePicker(today),
        jsdate: today,
        epoc: today.getTime()
      };
      
      firstPickupAddress.patchValue({ date: dateModel });

      // Add 15 minutes to now, then floor to the nearest 15-min boundary.
      // e.g. 11:09 + 15 = 11:24 → floor → 11:15
      //      11:30 + 15 = 11:45 → floor → 11:45
      //      11:46 + 15 = 12:01 → floor → 12:00
      const now = new Date();
      const msIn15Min = 15 * 60 * 1000;
      const pickupTime = new Date(Math.floor((now.getTime() + msIn15Min) / msIn15Min) * msIn15Min);
      // Use a timeout longer than setStoredAddresses (500ms) so our patch
      // runs last and isn't overwritten by any intermediate re-render.
      setTimeout(() => {
        const pickupSitesCtrl = this.bookingForm.get('pickup_sites.addresses') as FormArray;
        pickupSitesCtrl.at(0).get('opening_time').setValue(
          { hour: pickupTime.getHours(), minute: pickupTime.getMinutes() }
        );
        console.log(`Pre-filled pickup time to ${pickupTime.getHours()}:${String(pickupTime.getMinutes()).padStart(2,'0')} (now + 15 min)`);
      }, 600);
    }

    // For iDrv5 bookings, pre-fill the delivery date using business_days from
    // the selected rate entry (today + N business days, skipping weekends).
    if (this.isIdrv5Booking && this.quoteData.delivery_business_days != null) {
      const deliveryDate = this.addBusinessDays(new Date(), this.quoteData.delivery_business_days);
      const deliveryDateModel = {
        date: {
          year: deliveryDate.getFullYear(),
          month: deliveryDate.getMonth() + 1,
          day: deliveryDate.getDate()
        },
        formatted: this.formatDatePicker(deliveryDate),
        jsdate: deliveryDate,
        epoc: deliveryDate.getTime()
      };
      firstDeliveryAddress.patchValue({ date: deliveryDateModel });
      console.log(
        `iDrv5: pre-filled delivery date to ${deliveryDate.toDateString()} ` +
        `(${this.quoteData.delivery_business_days} business days from today)`
      );
    }

    firstPickupAddress.patchValue({
      suburb: this.quoteData.pickup_suburb,
      state: this.quoteData.pickup_state,
      postal_code: this.quoteData.pickup_postcode,
      is_hand_unload: this.quoteData.is_hand_unload,
      is_trailer_split: this.quoteData.is_trailer_split,
      is_tail_lift: this.quoteData.pickup_is_tail_lift || false,
      premise_type_id: this.quoteData.pickup_premise_type_id || 2 // default to Commercial
    });

    firstDeliveryAddress.patchValue({
      suburb: this.quoteData.delivery_suburb,
      state: this.quoteData.delivery_state,
      postal_code: this.quoteData.delivery_postcode,
      is_hand_unload: this.quoteData.is_hand_unload,
      is_trailer_split: this.quoteData.is_trailer_split,
      is_tail_lift: this.quoteData.delivery_is_tail_lift || false,
      premise_type_id: this.quoteData.delivery_premise_type_id || 2 // default to Commercial
    });
    
    console.log("Patched booking form", this.bookingForm);

    this.setStoredAddresses()
  }

  private setStoredAddresses() {
    setTimeout(() => {
      // For Pickup Addresses
      const pickupAddresses = this.getPickupAddressControl();
      pickupAddresses.forEach((_, index) => {
        const pickupInput = document.querySelector(`ngx-geo-autocomplete#pickup_auto_location-${index} input`) as HTMLInputElement;
        if (pickupInput && !pickupInput.value) {
          const addressToUse = this.pickupAddressesChanged[index] ? 
            this.currentPickupAddresses[index] : 
            (index === 0 ? this.quoteData.pickup_full_suburb : '');
            
          if (addressToUse) {
            pickupInput.value = addressToUse;
            const event = new Event('input', { bubbles: true });
            pickupInput.dispatchEvent(event);
          }
        }
      });

      // For Delivery Addresses
      const deliveryAddresses = this.getDeliveryAddressControl();
      deliveryAddresses.forEach((_, index) => {
        const deliveryInput = document.querySelector(`ngx-geo-autocomplete#delivery_auto_location-${index} input`) as HTMLInputElement;
        if (deliveryInput && !deliveryInput.value) {
          const addressToUse = this.deliveryAddressesChanged[index] ? 
            this.currentDeliveryAddresses[index] : 
            (index === 0 ? this.quoteData.delivery_full_suburb : '');
            
          if (addressToUse) {
            deliveryInput.value = addressToUse;
            const event = new Event('input', { bubbles: true });
            deliveryInput.dispatchEvent(event);
          }
        }
      });
    }, 500);
  }



  getPickupAddressControl() {
    const ptCtrl = (<any>this.bookingForm).controls.pickup_sites;
    const addCtrl = ptCtrl.controls.addresses;
  
    return addCtrl.controls;
  }

  getDeliveryAddressControl() {
    const ptCtrl = (<any>this.bookingForm).controls.delivery_sites;
    const addCtrl = ptCtrl.controls.addresses;
  
    return addCtrl.controls;
  
  }

  private initCustomerFormGroup() {
    const email = "^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,12}$";
    
    return this.fb.group({
      company: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern(email)]],
      contact_no: ['' , Validators.required],
      contact_name: ['' , Validators.required]
    });
  }

  private initBillToFormGroup() {
    return this.fb.group({
      company: [''],
      address: [''],
      email: [''],
      contact_no: [''],
      contact_name: ['']
    });
  }

  private initPackagings() {
    const currencyRegex = '^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$';
    const floatRegex = '^[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?$';
    const intRegex = '^[0-9]{1,6}$';
    
    this.packaging_id += 1;
    return this.fb.group({
      packaging_id: [this.packaging_id],
      packaging_type_id: ['',Validators.required],
      packaging_type_name: [''],
      quantity: [1, [Validators.required, Validators.pattern(intRegex)]],
      length: ['', [Validators.required, Validators.pattern(floatRegex)]],
      width: ['', [Validators.required, Validators.pattern(floatRegex)]],
      height: ['', [Validators.required, Validators.pattern(floatRegex)]],
      weight: ['', [Validators.required, Validators.pattern(floatRegex)]],
      total_weight: [0],
      db_id: ['']
    });
  }

  private initPickupSitesGroup() {
    const group = this.fb.group({
      addresses: this.fb.array([
        this.initAddress(1)
      ])
    });
    return group;
  }

  private initDeliverySitesGroup() {
    const group = this.fb.group({
      addresses: this.fb.array([
        this.initAddress(2)
      ])
    });
    return group;
  }

  private initAddress(activityTypeId: number) {
    // Default pickup/delivery time to now + 15 min, floored to the nearest
    // 15-min boundary to match the timepicker's minuteStep=15.
    // e.g. 11:09 + 15 = 11:24 → floor → 11:15
    const msIn15Min = 15 * 60 * 1000;
    const d2 = new Date(Math.floor((Date.now() + msIn15Min) / msIn15Min) * msIn15Min);

    var hours = d2.getHours();
    var minutes = d2.getMinutes();

    return this.fb.group({
      id: [''],
      location_string: [''],
      lat: [''],
      long: [''],
      street_no: [{value: '', disabled: true}],
      street: [{value: '', disabled: true}, Validators.required],
      suburb: [{value: '', disabled: true}, Validators.required],
      state: ['', Validators.required],
      state_id: [''],
      postal_code: [{value: '', disabled: true}],
      activity_type_id: [activityTypeId],
      date: [{date: {year: d2.getFullYear(), month: d2.getMonth() + 1, day: d2.getDate()}}],
      opening_time: [{hour: hours, minute: minutes}],
      closing_time: [{hour: hours, minute: minutes}],
      company_name: [''],
      contact_name: [''],
      contact_no: [''],
      contact_email: [''],
      is_direct: [false],
      is_tail_lift: [false],
      is_hand_unload: [false],
      is_trailer_split: [false],
      is_extra_crew: [false],
      is_holding_overnight: [false],
      is_tailgate: [false],
      is_residential: [false],
      is_futile: [false],
      is_tba: [false],
      is_atl: [false],
      driver_id: [''],
      status_id: [''],
      order_no: [''],
      unit: [''],
      amount: [''],
      file: [''],
      notes: [''],
      business_open_time: [''],
      business_close_time: [''],
      packagings: [''],
      premise_type_id: [2]
    });
  }

  private initDGClassesGroup() {
    return this.fb.group({
      dg_class_id: [''],
      dg_type_id: ['1'],
      dg_file: [''],
      dg_filename: [''],
      subsidiaries: this.fb.array([])
    });
  }
  
  // initAddinItem() {
  //   const group = this.fb.group({
  //       connote_id: [''],
  //       connote_addin_id : [''],
  //       description: [''],
  //       units: [0],
  //       cost: [0.00],
  //       created_by: [this.user.id],
  //   });
  
  //   return group;
  // }

  setActiveTab(tab: string) {
    this.activeTab = tab;

    if (tab === 'pickup' || tab === 'delivery') {
      this.setStoredAddresses();
    }
  }

  addPickupAddress() {
    // add address to the list
    console.log('adding additional address');
    const ptCtrl = (<any>this.bookingForm).controls.pickup_sites;
    const control = <FormArray>ptCtrl.controls['addresses'];
    control.push(this.initAddress(1));
    let index = control.length - 1;
    this.currentPickupIndex = index;

    // Initialize the new address tracking
    this.pickupAddressesChanged[index] = false;
    this.currentPickupAddresses[index] = '';
  }  // END ADD ADDRESS
  
  addDeliveryAddress() {
    // add address to the list
    const ptCtrl = (<any>this.bookingForm).controls.delivery_sites;
    const control = <FormArray>ptCtrl.controls['addresses'];
    control.push(this.initAddress(2));
    let index = control.length - 1;
    
    this.currentDeliveryIndex = index;

    // Initialize the new address tracking
    this.deliveryAddressesChanged[index] = false;
    this.currentDeliveryAddresses[index] = '';
  }

  removePickupAddress(i: number) {
    // remove address from the list
    const remCtrl = (<any>this.bookingForm).controls.pickup_sites;
    const control = <FormArray>remCtrl.controls['addresses'];
    control.removeAt(i);

    // Remove the address tracking
    this.pickupAddressesChanged.splice(i, 1);
    this.currentPickupAddresses.splice(i, 1);
  }

  removeDeliveryAddress(i: number) {
    // remove address from the list
    const remCtrl = (<any>this.bookingForm).controls.delivery_sites;
    const control = <FormArray>remCtrl.controls['addresses'];
    control.removeAt(i);

    // Remove the address tracking
    this.deliveryAddressesChanged.splice(i, 1);
    this.currentDeliveryAddresses.splice(i, 1);
  }

  countPickups() {
    const ptCtrl = (<any>this.bookingForm).controls.pickup_sites;
    const control = <FormArray>ptCtrl.controls['addresses'];
    return control.length;
  }

  countDeliveries() {
    const ptCtrl = (<any>this.bookingForm).controls.delivery_sites;
    const control = <FormArray>ptCtrl.controls['addresses'];
    return control.length;
  }

  pickupAddressCallback(selectedData:any,i:number) {
    if (!this.pickupAddressesChanged[i]) {
      this.pickupAddressesChanged[i] = false;
      this.currentPickupAddresses[i] = '';
    }

    this.pickupAddressesChanged[i] = true;
    
    // Store the new address
    if (typeof selectedData === 'object' && selectedData.data) {
      this.currentPickupAddresses[i] = selectedData.data.formatted_address || 
                                     `${selectedData.data.name}, ${selectedData.data.vicinity}`;
    }
    

    const pCtrl = (<any>this.bookingForm).controls.pickup_sites;
    const pickupCtrl = <FormArray>pCtrl.controls['addresses'].controls[i];
    console.log("Pickup Input Address:",pickupCtrl);
  
    const latCtrl = pickupCtrl.controls["lat"];
    const longCtrl = pickupCtrl.controls["long"];
    const streetNoCtrl = pickupCtrl.controls["street_no"];
    const streetCtrl = pickupCtrl.controls["street"];
    const suburbCtrl = pickupCtrl.controls["suburb"];
    const stateCtrl = pickupCtrl.controls["state"];
    const stateIdCtrl = pickupCtrl.controls["state_id"];
    const postalCodeCtrl = pickupCtrl.controls["postal_code"];
    const companyCtrl =  pickupCtrl.controls["company_name"];
  
    latCtrl.reset();
      longCtrl.reset();
      streetNoCtrl.reset();
      streetCtrl.reset();
      suburbCtrl.reset();
      stateCtrl.reset();
      stateCtrl.reset();
      stateIdCtrl.reset();
      postalCodeCtrl.reset(); 
      companyCtrl.reset();
  
      if (typeof selectedData === 'string' || selectedData instanceof String)
        this.pointdata = selectedData;
      else
        this.pointdata = JSON.stringify(selectedData);
  
      if (selectedData) {
  
        this.mapObj = JSON.parse(this.pointdata);
        this.mapObj = this.mapObj.data;
        this.mapLat = this.mapObj.geometry.location.lat;
        this.mapLng = this.mapObj.geometry.location.lng;
        this.fillCompanyName(pickupCtrl, this.mapObj.name, this.mapObj.types);
        latCtrl.setValue(this.mapLat);
        longCtrl.setValue(this.mapLng);
  
        var arr = [];
        
        arr[0] = this.mapLat;
        arr[1] = this.mapLng;
            
        this.positions[i] = arr;
  
        // enable address form editing
        streetNoCtrl.enable();
        streetCtrl.enable();
        suburbCtrl.enable();
        postalCodeCtrl.enable();
       
        for(let result of this.mapObj.address_components){
            if (result.types == "street_number")
                streetNoCtrl.setValue(result.short_name);
            if (result.types == "route")
                streetCtrl.setValue(result.short_name);
            if ( result.types.includes("locality") ) 
                suburbCtrl.setValue(result.short_name);
            if ( result.types.includes("postal_code") ) 
                postalCodeCtrl.setValue(result.short_name);
            if ( result.types.includes("administrative_area_level_1") ) {
              let stateId = null;
              console.log("state", this.states);
              console.log("result?", result);
              
              this.states.forEach((item) => {
                  if (item.abbreviation === result.short_name) {
                      stateId = item.id;
                  }
              });
              stateIdCtrl.setValue(stateId); 
              stateCtrl.setValue(result.short_name);
            }
        }
        console.log("Pickup Address Component:", this.mapObj.address_components);
        
  
        //check if location is already in database and reload set open and close business hours
        this.checkBusinessHours(pickupCtrl.controls);
        
      }

    // To ensure form is updated
    setTimeout(() => {
      this.recomputeBookingRate();
    }, 100);
   
  }

  deliveryAddressCallback(selectedData:any,i:number) {
     if (!this.deliveryAddressesChanged[i]) {
      this.deliveryAddressesChanged[i] = false;
      this.currentDeliveryAddresses[i] = '';
    }

    this.deliveryAddressesChanged[i] = true;
    
    // Store the new address
    if (typeof selectedData === 'object' && selectedData.data) {
      this.currentDeliveryAddresses[i] = selectedData.data.formatted_address || 
                                       `${selectedData.data.name}, ${selectedData.data.vicinity}`;
    }

    const dCtrl = (<any>this.bookingForm).controls.delivery_sites;
    const deliveryCtrl = <FormArray>dCtrl.controls['addresses'].controls[i];
    console.log("Delivery Input Address:", deliveryCtrl);
  
  
    const latCtrl = deliveryCtrl.controls["lat"];
    const longCtrl = deliveryCtrl.controls["long"];
    const streetNoCtrl = deliveryCtrl.controls["street_no"];
    const streetCtrl = deliveryCtrl.controls["street"];
    const suburbCtrl = deliveryCtrl.controls["suburb"];
    const stateCtrl = deliveryCtrl.controls["state"];
    const stateIdCtrl = deliveryCtrl.controls["state_id"]
    const postalCodeCtrl = deliveryCtrl.controls["postal_code"];
    const companyCtrl =  deliveryCtrl.controls["company_name"];
  
    latCtrl.reset();
      longCtrl.reset();
      streetNoCtrl.reset();
      streetCtrl.reset();
      suburbCtrl.reset();
      stateCtrl.reset();
      stateCtrl.reset();
      stateIdCtrl.reset();
      postalCodeCtrl.reset(); 
      companyCtrl.reset()
  
      if (typeof selectedData === 'string' || selectedData instanceof String)
        this.pointdata = selectedData;
      else
        this.pointdata = JSON.stringify(selectedData);
  
      if (selectedData) {
  
        this.mapObj = JSON.parse(this.pointdata);
        this.mapObj = this.mapObj.data;
        this.mapLat = this.mapObj.geometry.location.lat;
        this.mapLng = this.mapObj.geometry.location.lng;
        this.fillCompanyName(deliveryCtrl, this.mapObj.name, this.mapObj.types);
        latCtrl.setValue(this.mapLat);
        longCtrl.setValue(this.mapLng);
  
        var arr = [];
        
        arr[0] = this.mapLat;
        arr[1] = this.mapLng;
            
        this.positions[i] = arr;
       
        // enable address form editing
        streetNoCtrl.enable();
        streetCtrl.enable();
        suburbCtrl.enable();
        postalCodeCtrl.enable();
        
        for(let result of this.mapObj.address_components){
            if (result.types == "street_number")
                streetNoCtrl.setValue(result.short_name);
            if (result.types == "route")
                streetCtrl.setValue(result.short_name);
            if ( result.types.includes("locality") ) 
                suburbCtrl.setValue(result.short_name);
            if ( result.types.includes("postal_code") ) 
                postalCodeCtrl.setValue(result.short_name);
            if ( result.types.includes("administrative_area_level_1") ) {
              let stateId = null;
              this.states.forEach((item) => {
                  if (item.abbreviation === result.short_name) {
                      stateId = item.id;
                  }
              });
              stateIdCtrl.setValue(stateId); 
              stateCtrl.setValue(result.short_name);
            }
        }
        console.log("Delivery Address Components:", this.mapObj.address_components);
  
        //check if location is already in database and reload set open and close business hours
        this.checkBusinessHours(deliveryCtrl.controls);
         
      }

    // To ensure form is updated
    setTimeout(() => {
      this.recomputeBookingRate();
    }, 100);
  }

  fillCompanyName(control, name, types){
    let Ctrl;
    Ctrl = control.controls['company_name'];
    const companyNameCtrl = Ctrl;
  
    if(types.find(x=>x == "establishment") != undefined) {
      let result = this.customers?.find(x=> x?.name?.toLowerCase() == name.toLowerCase());
      if(result != undefined){
        companyNameCtrl.setValue(result.name);
      } else {
        let address_name = name.toLowerCase().split(' ')

        const keyword = this.customers?.filter(x=> { 
          return (x?.name?.toLowerCase()).includes(address_name[0])
        }) || [];
        
        if(keyword?.length !=0){
          if(keyword.length == 1){
            companyNameCtrl.setValue(name);
          } else {
            let result = keyword.find(x=>(x?.name?.toLowerCase()).includes(address_name[1]))
            if(result != undefined) companyNameCtrl.setValue(name); else companyNameCtrl.setValue('');
          }
        } else companyNameCtrl.setValue('');
      }
    } else companyNameCtrl.setValue('');
  }

  checkBusinessHours(siteCtrl){

    if(siteCtrl.lat.value || siteCtrl.long.value){
      let lat = siteCtrl.lat.value;
      let lng = siteCtrl.long.value;

      this.bookService.getLocationRecord(lat,lng).subscribe(data=>{console.log(data);
        if(data["business_hours"]!=null){
          let hours = data["business_hours"];
          if(hours['business_open_time']){
            siteCtrl.business_open_time.setValue(hours['business_open_time']);
            siteCtrl.business_close_time.setValue(hours['business_close_time']);
          }else{
            siteCtrl.business_open_time.setValue('');
            siteCtrl.business_close_time.setValue('');
          };
        }else{
          siteCtrl.business_open_time.setValue('');
          siteCtrl.business_close_time.setValue('');
        };        
      });
    }
  }

  getJobFile(event, type){
    (type == 10) ? console.log("DG File ", event) : console.log("Doc File ", event);
    let allowedExtension = ['pdf','png','jpg','gif'];
    var doc_filename ='';
    var dg_filename ='';

    if(event == null){
      if(type == 10) this.dg_file = event;
      else if(type == 11) this.doc_file = event;
    }
    else{
      let selectedFilename = event.file.name.split('.');
      let selectedFileExtension = selectedFilename[selectedFilename.length - 1].toLowerCase();
      console.log('selected file: ',selectedFilename);

      if( allowedExtension.includes(selectedFileExtension) ){
        let file: ImageResult;
        file = event;
        if(type == 10){
          this.dg_file = event;
          dg_filename = file.file.name;
        } 
        else if(type == 11){
          this.doc_file = event;
          doc_filename = file.file.name;
        };
      }
      else{
        this.toastr.error('','Error: Extension Not Allowed', {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true,
        });
      }
    }
    
    //set to form files
    if(type == 11){
      this.bookingForm.controls.connote_file.setValue(this.doc_file);
      this.bookingForm.controls.connote_filename.setValue(doc_filename);
    }

    if(type == 10){
      const dg_ctrl = (<any>this.bookingForm).controls.dgClass.controls.dg_file;
      dg_ctrl.setValue(this.dg_file);
      const dg2_ctrl = (<any>this.bookingForm).controls.dgClass.controls.dg_filename;
      dg2_ctrl.setValue(dg_filename);
      
    }
  }

  // Method to get the appropriate created_by value
  getCreatedBy(): number {
    if (this.isDyspachBooking) {
      console.log('Using Dyspach created_by:', this.userMapping.dyspach_created_by);
      return this.userMapping.dyspach_created_by;
    }
    if (this.isIdrv5Booking) {
      // Use the same mapped user as Dyspach if available, otherwise fall back
      const idrv5CreatedBy = this.userMapping?.idrv5_created_by || this.userMapping?.dyspach_created_by || 1;
      console.log('Using iDrv5 created_by:', idrv5CreatedBy);
      return idrv5CreatedBy;
    }
    console.log('Using fallback created_by: 1');
    return 1;
  }


  isSubmit: boolean = false;
  onSubmit() {
    if (this.isSubmit) return;
    // console.log(this.bookingForm.value);

    this.isSubmit = true;

    // Set the appropriate created_by value before submission
    const createdBy = this.getCreatedBy();
    this.bookingForm.patchValue({
      created_by: createdBy
    });

    // If this is a Dyspach quote, add the Dyspach quote request ID to the form data
    if (this.isDyspachBooking && this.dyspachQuoteRequestId) {
      this.bookingForm.patchValue({
        outside_reference_no: this.dyspachQuoteRequestId,
        outside_quote_origin: 'dyspach'
      });
    }

    // If this is an iDrv5 quote, ensure the outside reference number is in the form data
    if (this.isIdrv5Booking && this.quoteData?.outside_reference_no) {
      this.bookingForm.patchValue({
        outside_reference_no: this.quoteData.outside_reference_no,
        outside_quote_origin: 'idrv5'
      });
    }
    
    this.bookService.savePublicBooking(this.bookingForm.value).subscribe(data=>{
      var error = data['error'];
      var message = data['message'];

      if(error){
        this.toastr.error(message, 'Submit Booking Error');
        this.isSubmit = false;
      }else{
        this.toastr.success(message, 'Submit Booking Success');
        this.goToQuotes();
      }
      
    })
  }
  
  
    onCancel() {
    this.router.navigate(['/book']);
  }

  goToQuotes(){
    this.router.navigate(['']);
  }


  useCompanyBilling: boolean = true;
  isBillingChecked() {
    const ctrl = (<any>this.bookingForm).controls.bill_to;
  
    var element = <HTMLInputElement> document.getElementById("billCheck");
    this.useCompanyBilling = element.checked;
    if (this.useCompanyBilling) {
      ctrl.controls.company.setValue('');
      ctrl.controls.address.setValue('');
      ctrl.controls.email.setValue('');
      ctrl.controls.contact_no.setValue('');
    }
  }

  public DG_TYPE = {
    NONE:     0,
    PACKED:   1,
    BULK:     2
  }

  showDGForm: boolean = false;
  dgLicenses: any = [];

  toggleAddDG() {
    this.showDGForm = !this.showDGForm;
  
    if (this.showDGForm) {
      this.setDGType(this.DG_TYPE.PACKED);
    } else {
      const dgCtrl = (<any>this.bookingForm).controls.is_dg;
      dgCtrl.setValue(true);
      this.setDGType(this.DG_TYPE.NONE);
    }
  }

  setDGType(type: number) {
    const dgCtrl = (<any>this.bookingForm).controls.dgClass;
    const tCtrl = dgCtrl.controls.dg_type_id;
    tCtrl.setValue(type);
  }

  getDGType() {
    // update dg type value
    const dgCtrl = (<any>this.bookingForm).controls.dgClass;
    const tCtrl = dgCtrl.controls.dg_type_id;
    return tCtrl.value;
  }

  onDGSelectChange(selectedId) {
    const dgCtrl = (<any>this.bookingForm).controls.is_dg;
    dgCtrl.setValue(true);
    //console.log(pptCtrl.value);
  } // end onDGCheckChange


  onSubsidiaryChange(event) {
    const formArray: FormArray = this.bookingForm.get('dgClass.subsidiaries') as FormArray;

    /* Selected */
    if (event.target.checked) {
        // Add a new control in the arrayForm
        formArray.push(new FormControl(event.target.value));
    }
    /* unselected */
    else {
        // find the unselected element
        let i: number = 0;

        formArray.controls.forEach((ctrl: FormControl) => {
            if (ctrl.value == event.target.value) {
                // Remove the unselected element from the arrayForm
                formArray.removeAt(i);
                return;
            }

            i++;
        });
    }

    const pptCtrl = (<any>this.bookingForm).controls.dgClass;
  } // end onDGCheckChange

  checkSubsidiaryStatus(int: number) {
    const sCtrl = (<any>this.bookingForm).controls.dgClass.controls.subsidiaries;
    
    if (sCtrl.value.includes(int.toString() ) )
        return true;
    else return false;

  }

  isDGMainClass(int: number) {
    const dgCtrl = (<any>this.bookingForm).controls.dgClass.controls.dg_class_id;
    if (dgCtrl.value == int.toString() )          
        return true;
    else return false;

  }

  getSubsidiaries() {
    const sCtrl = (<any>this.bookingForm).controls.dgClass.controls.subsidiaries;
    var shortStr: string = "";
    var longStr: string = "";
    var count = 0;
    this.dgLicenses.forEach(function (e) {
        if (sCtrl.value.includes(e.id.toString())) {
            count = count + 1;
            if (count > 1) {
                shortStr = shortStr + "/";
                longStr = longStr + "/"
            }
            shortStr = shortStr + e.name.replace("Class",'');
            longStr = longStr + e.name + "-" + e.description;
        }

    });
    return shortStr;


  }
  //clear checked subsidiaries
  clearSubsidiaries() {
    const formArray: FormArray = this.bookingForm.get('dgClass.subsidiaries') as FormArray;
    while (formArray.length !== 0) {
        formArray.removeAt(0)
    }
    console.log('subsidiaries cleared.')
  }

  hasSubsidiaryPick() {
    const pCtrl = (<any>this.bookingForm).controls.dgClass.controls.subsidiaries;

    if (pCtrl.value.length > 0)
        return true;
    else return false;
  }

  hasDGPick() {
    const DGCtrl = (<any>this.bookingForm).controls.dgClass;
    const classesCtrl = DGCtrl.controls.dg_class_id;
    const typeCtrl = DGCtrl.controls.dg_type_id;

    if (classesCtrl.value != '' && typeCtrl.value != null && this.showDGForm)
        return true;
    else return false;
  }
  
  showPackagingForm: boolean = true;
  toggleAddPackaging() {
    this.showPackagingForm = !this.showPackagingForm;
  }

  public packaging_id: number =0;
  public plc_packagings: any =[];
  packagingTypes: any =[];

  setPackageTypeName(i, event){
    
    const pCtrl = (<any>this.bookingForm).controls.packagings;

    if(event.target.value !=''){
      let type_name= '';
      this.packagingTypes.forEach(pt=>{
        if(pt.id.toString() == event.target.value){
          type_name = pt.name;
          pCtrl.controls[i].controls['packaging_type_name'].setValue(type_name);
        }
      })
    };
    
    this.plc_packagings = pCtrl.value;
  }

  is_oversized :boolean = false;
  is_pallet : boolean = false;
  costDefaults: any;

  public PACKAGING_TYPE_LABELS = {
    PLAINPALLET: 'Plain Pallet',
    CARTON: 'Carton',
    SKID: 'Skid',
    OVERPLAINPALLET: 'Oversized Plain Pallet',
    CHEPPALLET: 'Chep Pallet',
    OVERCHEPPALLET: 'Oversized Chep Pallet',
    LOSCOMPALLET: 'Loscom Pallet',
    OVERLOSCOMPALLET: 'Oversized Loscom Pallet',
  }

  public checkPalletDimensions(index){
    this.is_oversized = false;
    this.is_pallet = false;
    const pCtrl = (<any>this.bookingForm).controls.packagings.controls[index];
    const pallet_type = pCtrl.controls['packaging_type_id'].value;
    let pallet_id = 0;
    let oversized_pallet_id = 0;
    let plainp_id = 0;
    let oversized_plainp_id = 0;
    let chep_id = 0;
    let oversized_chep_id = 0;
    let loscom_id = 0;
    let oversized_loscom_id = 0;
    
      if(this.packagingTypes?.length > 0){
        this.packagingTypes.forEach(pt => {
          
          if(pt.name == this.PACKAGING_TYPE_LABELS.PLAINPALLET){
            plainp_id = pt.id;
          }

          if(pt.name == this.PACKAGING_TYPE_LABELS.OVERPLAINPALLET){
            oversized_plainp_id = pt.id;
          }

          if(pt.name == this.PACKAGING_TYPE_LABELS.CHEPPALLET){
            chep_id = pt.id;
          }

          if(pt.name == this.PACKAGING_TYPE_LABELS.OVERCHEPPALLET){
            oversized_chep_id = pt.id;
          }

          if(pt.name == this.PACKAGING_TYPE_LABELS.LOSCOMPALLET){
            loscom_id = pt.id;
          }

          if(pt.name == this.PACKAGING_TYPE_LABELS.OVERLOSCOMPALLET){
            oversized_loscom_id = pt.id;
          }
        });
      }
      

      //assign to pallet id and oversized pallet id
      if(pallet_type == plainp_id || pallet_type == oversized_plainp_id){
        pallet_id = plainp_id;
        oversized_pallet_id = oversized_plainp_id;
      }

      if(pallet_type == chep_id || pallet_type == oversized_chep_id){
        pallet_id = chep_id;
        oversized_pallet_id = oversized_chep_id;
      }

      if(pallet_type == loscom_id || pallet_type == oversized_loscom_id){
        pallet_id = loscom_id;
        oversized_pallet_id = oversized_loscom_id;
      }
    
    //check dimensions if selected packaging type is either Pallet type or Oversized Palle type
    if(pallet_id !=0 && oversized_pallet_id !=0){
      if(pallet_type && (pallet_type == pallet_id || pallet_type == oversized_pallet_id)){
        let max_width = parseFloat(this.costDefaults.pallet_max_width);
        let max_length = parseFloat(this.costDefaults.pallet_max_length);
        let max_height = parseFloat(this.costDefaults.pallet_max_height);
        let max_weight = parseFloat(this.costDefaults.pallet_max_weight);

        if(pallet_type == pallet_id &&
           (parseFloat(pCtrl.controls['length'].value) > max_length ||
            parseFloat(pCtrl.controls['width'].value) > max_width ||
            parseFloat(pCtrl.controls['height'].value) > max_height ||
            parseFloat(pCtrl.controls['weight'].value) > max_weight)  ){
          pCtrl.controls['packaging_type_id'].setValue(oversized_pallet_id);
          this.is_oversized = true;
          this.is_pallet = false;
        };
    
        if(pallet_type == oversized_pallet_id &&
          (parseFloat(pCtrl.controls['length'].value) <= max_length &&
           parseFloat(pCtrl.controls['width'].value) <= max_width &&
           parseFloat(pCtrl.controls['height'].value) <= max_height &&
           parseFloat(pCtrl.controls['weight'].value) <= max_weight) ){
          pCtrl.controls['packaging_type_id'].setValue(pallet_id);
          this.is_pallet = true;
          this.is_oversized = false;
        };    
      }else{
        this.is_pallet = false;
        this.is_oversized = false;
      };
    }
  };

  addPackaging() {
    const pCtrl = (<any>this.bookingForm).controls.packagings;
    pCtrl.push(this.initPackagings());
    let index = pCtrl.length - 1;

    this.plc_packagings= pCtrl.value;
  }


  removePackaging(i: number) {
    const pCtrl = (<any>this.bookingForm).controls.packagings;
    let p_id = pCtrl.controls[i].controls.packaging_id.value;
    pCtrl.removeAt(i);
    this.positions.splice(i, 1);
    this.getBookingPallets('packaging');this.getBookingWeight();
    this.plc_packagings= pCtrl.value;
  }

  public totalChargeablePallets =0; 
  public totalChargeableWeight =0; 
  public totalWeightKg =0; 
  public pallet_units:  number = 0;
  public total_qty :number= 0;
  public is_unit_auto_compute = true;
  getBookingPallets(caller) {
    const pCtrl = (<any>this.bookingForm).controls.packagings.controls;
    this.totalChargeablePallets = 0;
    this.totalChargeableWeight =0; 
    this.totalWeightKg = 0;
    this.pallet_units = 0;
    this.total_qty = 0;
    // this.packaging_volume_total = 0;
    // this.is_max_capacity = false;
    var cubic_weight_param = 333;
    if(this.costDefaults?.cubic_weight_param != null){
      cubic_weight_param = this.costDefaults.cubic_weight_param;
    }

    pCtrl.forEach((item) => {
      
      if(item.controls.length.value && item.controls.width.value && item.controls.height.value && item.controls.quantity.value && item.controls.weight.value) {
        if(item.controls.quantity.value){this.total_qty += parseFloat(item.controls.quantity.value)};
        var volume = item.controls.quantity.value * ( (item.controls.length.value/100) * (item.controls.width.value/100) * (item.controls.height.value)/100 );
        var cubicWeight = volume * cubic_weight_param;
        var physicalWeight = item.controls.total_weight.value;
        var chargeWeight = Math.max(physicalWeight,cubicWeight);
        var chargeablePallets = chargeWeight / 1000;
        this.totalChargeablePallets = this.totalChargeablePallets + chargeablePallets;
        this.totalChargeableWeight = this.totalChargeableWeight + chargeWeight;

        var tonne_weight = item.controls.weight.value/1000;
        this.totalWeightKg = this.totalWeightKg + tonne_weight;

        // this.packaging_volume_total += volume; 
      }
    });
    
    if(this.totalChargeablePallets > 0)
    {
      this.totalChargeablePallets = parseFloat(this.totalChargeablePallets.toFixed(2));
    } 
    
    this.totalChargeablePallets = Math.ceil(this.totalChargeablePallets);
    
    if(this.is_unit_auto_compute == true || caller == 'user'){
      // this.bookingFrm.controls.pallet.setValue(0);
      if(this.total_qty < this.totalChargeablePallets){// only set chargeable pallet to pallet units if it is greater in value
        this.bookingForm.controls.pallet.setValue(this.totalChargeablePallets);
        this.pallet_units = this.totalChargeablePallets;
      }else{
        this.bookingForm.controls.pallet.setValue(this.total_qty);
        this.pallet_units = this.total_qty;
        this.totalChargeablePallets = this.total_qty;
      };
    }
    
    if(this.is_unit_auto_compute == true || caller == 'user'){
      this.bookingForm.controls.tonnes.setValue(this.totalWeightKg.toFixed(2));
    };

    // var st_id = this.bookingForm.controls.service_type_id.value;
    // if(st_id){
    //   this.sv_volume = this.getServiceTypeVolumeCapacity(st_id);
    //   this.sv_weight = this.getServiceTypeWeightCapacity(st_id);
    //   this.is_max_capacity = this.getLoadCapacity(this.packaging_volume_total, this.weight, this.sv_volume, this.sv_weight);console.log(this.sv_volume, this.sv_weight, this.is_max_capacity)
    // }
    
    //return this.totalChargeablePallets;
    
    // To ensure form is updated
    setTimeout(() => {
      this.recomputeBookingRate();
    }, 100);
  }

  getPackagingControl() {
    const pCtrl = (<any>this.bookingForm).controls.packagings;
    
    return pCtrl.controls;
  }

  showPackagingRemoveIcon() {
    if (this.getPackagingControl().length > 1)
        return true;
    else return false;
  }

  showPackagingClearIcon() {
    if (this.getPackagingControl().length == 1)
        return true;
    else return false;
  }

  public weight = 0;
  public tonne =0;
  getBookingWeight() {
    const pCtrl = (<any>this.bookingForm).controls.packagings.controls;
    this.weight = 0;

    pCtrl.forEach((item) => {
      if(item.controls.total_weight) {
      this.weight = this.weight + item.controls.total_weight.value;
        
      }
    });
    this.tonne = this.weight/1000;
  }

  assignTotalWeight(i: number, caller) {
    const pCtrl = (<any>this.bookingForm).controls.packagings.controls[i];
    if (pCtrl.controls['weight'].value)
        var weight = parseFloat(pCtrl.controls['weight'].value);
    else var weight = 0;
    if (pCtrl.controls['quantity'].value)
        var quantity = parseInt(pCtrl.controls['quantity'].value);
    else var quantity = 0;

    if (quantity && weight) {
        var number = quantity * weight;
        pCtrl.controls['total_weight'].setValue(number);

    }

    this.getBookingPallets(caller);this.getBookingWeight();

    //refresh plc_packagings per packagings display per site
    this.plc_packagings = (<any>this.bookingForm).controls.packagings.value;
  }

  createGetBookingPayload() {
    const formValue = this.bookingForm.value;console.log("Delivery_Sites", formValue.delivery_sites);
    
    const payload = {
      pickup_sites: formValue.pickup_sites.addresses,
      delivery_sites: formValue.delivery_sites.addresses,
      chargeable_pallets: formValue.pallet,
      dg_class : formValue.dgClass,
    };

    return payload;
  }

  price_msg : boolean = false;
  recomputeBookingRate(){
    // Skip recomputation for Dyspach quotes - use the original Dyspach price
    if (this.isDyspachBooking) {
      console.log('Skipping rate recomputation for Dyspach quote, keeping original price:', this.quoteData.est_total_price);
      this.bookingForm.controls.price.setValue(this.quoteData.est_total_price);
      return;
    }

    // Skip recomputation for iDrv5 quotes - price comes from rate_entry_grand_amount
    if (this.isIdrv5Booking) {
      console.log('Skipping rate recomputation for iDrv5 quote, keeping original price:', this.quoteData.est_total_price);
      this.bookingForm.controls.price.setValue(this.quoteData.est_total_price);
      this.est_total_price = this.quoteData.est_total_price;
      return;
    }

    // Use form.value consistently for both validation and payload
    const formValue = this.bookingForm.value;
    const pickupSites = formValue.pickup_sites?.addresses || [];
    const deliverySites = formValue.delivery_sites?.addresses || [];
    
    console.log('Pickup sites from form value:', pickupSites);
    console.log('Delivery sites from form value:', deliverySites);
    
    // Check if addresses are valid using the same data source
    const pickupValid = pickupSites.every(site => 
      site.suburb && site.state && site.postal_code
    );
    
    const deliveryValid = deliverySites.every(site => 
      site.suburb && site.state && site.postal_code
    );

    //only recompute if there is changes in delivery and pickup sites (for non-Dyspach quotes)
    if(pickupSites.length > 0 && deliverySites.length > 0 && pickupValid && deliveryValid){
      const payload = this.createGetBookingPayload();
      console.log("Get booking payload", payload);

      this.bookService.getBookingRates(payload).subscribe({
        next: (response: any) => {
          console.log("Booking Rates", response);
          if(this.quoteData.service_option == 'economy'){
            this.est_total_price = response.rates[0].economy_total_rate;
          }else{
            this.est_total_price = response.rates[0].express_total_rate;
          };

          this.bookingForm.controls.price.setValue(this.est_total_price);
          
          if(this.est_total_price == 0){
            this.price_msg = true;
          }else{
            this.price_msg = false;
          };
        },
        error: (error: any) => {
          console.error("Error booking rates",error);
        }
      })
    };    
  }

  checkingDuplicate: boolean = false; // Add loading state for duplicate check
  checkForExistingBooking(quote_id: number | null, outside_reference_no: string | null) {
    this.checkingDuplicate = true;
    // Keep the spinner visible while the duplicate check is in-flight.
    // The counter-based startLoading/finishLoading calls in the data-fetch
    // methods will take over once proceedWithBookingFlow is called.
    this.loading = true;

    const checkParams = {
      quote_id: quote_id,
      outside_reference_no: outside_reference_no
    };

    console.log('Checking for existing booking with params:', checkParams);

    this.bookService.checkBookingExists(checkParams).subscribe({
      next: (response: any) => {
        console.log('Duplicate check response:', response);
        
        if (response.exists) {
          // Booking already exists
          this.toastr.error(response.message, 'Booking Already Exists', {
            timeOut: 8000,
            positionClass: 'toast-top-full-width',
            progressBar: true
          });
          
          console.log('Duplicate booking found, redirecting to home');
          this.router.navigate(['']);
          return;
        } else {
          // No duplicate found, proceed with normal flow
          console.log('No duplicate booking found, proceeding normally');
          this.proceedWithBookingFlow(quote_id, outside_reference_no);
        }
      },
      error: (error: any) => {
        console.error('Error checking for existing booking:', error);
        this.toastr.error('Unable to verify booking status. Please try again.', 'Error');
        this.router.navigate(['']);
      },
      complete: () => {
        this.checkingDuplicate = false;
      }
    });
  }
  
  proceedWithBookingFlow(quote_id: number | null, outside_reference_no: string | null) {
    if (quote_id) {
      // Internal quote flow
      this.getQuoteData(quote_id);
    } else if (outside_reference_no) {
      if (this.isIdrv5Booking) {
        // iDrv5 widget flow — pass the persisted service level so the correct
        // rate entry is selected even after the async duplicate check completes
        this.getIdrv5Quote(outside_reference_no, this.idrv5ServiceLevelId);
      } else {
        // Dyspach quote flow
        this.getDyspachQuote(outside_reference_no, this._Activatedroute.snapshot.queryParams['rateID']);
      }
    }
  }
}
